import { useState, useCallback, useRef } from "react";
import BucketApi from "api/bucketApi";

const useAudienciaEditorImages = (procesoIdentificador, audienciaId, etapaNombre) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const imageCountersRef = useRef({});

  const setupImageHandler = useCallback(
    (quillRef, campoNombre, onImageUploaded = null) => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();
      const toolbar = quill.getModule("toolbar");

      toolbar.addHandler("image", () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.onchange = async () => {
          const file = input.files[0];
          if (file) {
            await handleImageUpload(file, quillRef, campoNombre, onImageUploaded);
          }
        };
        input.click();
      });
    },
    [audienciaId, etapaNombre]
  );

  const handleImageUpload = useCallback(
    async (file, quillRef, campoNombre, onImageUploaded = null) => {
      setImageError(null);
      const validation = BucketApi.validateImageFile(file);
      if (!validation.isValid) {
        setImageError(validation.errors.join(" "));
        return false;
      }
      try {
        setUploadingImage(true);
        // Redimensionar si es necesario
        let processedFile = file;
        if (file.size > 1024 * 1024) {
          processedFile = await BucketApi.resizeImage(file, 800, 600, 0.8);
        }
        // --- OBTENER TODAS las imágenes de la audiencia (todas las etapas) para análisis completo
        let allAudienciaImages = [];
        try {
          console.log("Obteniendo TODAS las imágenes de la audiencia para calcular consecutivo...");
          const allImagesResponse = await BucketApi.getAllAudienciaImages(
            procesoIdentificador,
            audienciaId
          );
          if (allImagesResponse?.data) {
            const allImagesData = allImagesResponse.data;
            if (Array.isArray(allImagesData)) {
              allAudienciaImages = allImagesData;
            } else if (allImagesData?.files && Array.isArray(allImagesData.files)) {
              allAudienciaImages = allImagesData.files;
            } else if (allImagesData?.objects && Array.isArray(allImagesData.objects)) {
              allAudienciaImages = allImagesData.objects;
            }
            // Normalizar nombres de archivo
            allAudienciaImages = allAudienciaImages
              .map((item) => {
                if (typeof item === "string") return item.split("/").pop().split("?")[0];
                if (item?.name) return item.name.split("/").pop().split("?")[0];
                if (item?.key) return item.key.split("/").pop().split("?")[0];
                return null;
              })
              .filter(Boolean);
          }
        } catch (error) {
          console.warn("No se pudo obtener la lista completa de imágenes de la audiencia:", error);
          allAudienciaImages = [];
        }
        console.log("Todas las imágenes existentes en la audiencia:", allAudienciaImages);
        // --- Determinar el siguiente consecutivo de manera más robusta
        //const escapeRegex = (s) => s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
        const etapaPrefix = `${audienciaId}_${etapaNombre}_${campoNombre}_`;
        console.log("Buscando archivos con prefijo:", etapaPrefix);
        // Función para extraer el número consecutivo del nombre del archivo
        const getConsecutivoFromName = (fileName) => {
          if (!fileName) return null;
          // Remover query params y path
          const cleanName = String(fileName).split("?")[0].split("/").pop();
          // Verificar si el archivo pertenece a esta etapa y campo específico
          if (!cleanName.startsWith(etapaPrefix)) {
            return null;
          }
          // Extraer el número consecutivo (después del último guión bajo antes de la extensión)
          const nameWithoutPrefix = cleanName.substring(etapaPrefix.length);
          const parts = nameWithoutPrefix.split(".");
          if (parts.length >= 2) {
            // El consecutivo debería ser la primera parte (antes de la extensión)
            const consecutivo = parseInt(parts[0], 10);
            return Number.isFinite(consecutivo) ? consecutivo : null;
          }
          return null;
        };

        // Obtener todos los consecutivos existentes para esta etapa y campo específico
        const existingConsecutivos = allAudienciaImages
          .map(getConsecutivoFromName)
          .filter((n) => Number.isFinite(n))
          .sort((a, b) => a - b);
        console.log(`Consecutivos existentes para ${etapaPrefix}:`, existingConsecutivos);
        // Obtener consecutivos de la sesión actual para este campo específico
        const sessionConsecutivos = uploadedImages
          .filter((img) => img.campo === campoNombre && img.fileName.startsWith(etapaPrefix))
          .map((img) => getConsecutivoFromName(img.fileName))
          .filter((n) => Number.isFinite(n))
          .sort((a, b) => a - b);
        console.log(`Consecutivos de sesión para ${campoNombre}:`, sessionConsecutivos);
        // Combinar todos los consecutivos usados
        const allUsedConsecutivos = [...new Set([...existingConsecutivos, ...sessionConsecutivos])];
        console.log("Todos los consecutivos usados:", allUsedConsecutivos);
        // Encontrar el siguiente consecutivo disponible
        let nextConsecutivo = 1;
        while (allUsedConsecutivos.includes(nextConsecutivo)) {
          nextConsecutivo++;
        }
        console.log(`Siguiente consecutivo disponible para ${campoNombre}:`, nextConsecutivo);
        // Actualizar contador local
        imageCountersRef.current[campoNombre] = nextConsecutivo;
        // Construir nombre final con el consecutivo correcto
        const extension = file.name.split(".").pop().toLowerCase();
        const fileName = `${audienciaId}_${etapaNombre}_${campoNombre}_${nextConsecutivo}.${extension}`;
        console.log("Nombre de archivo generado:", fileName);
        // Verificación adicional: asegurar que el archivo no existe
        let finalFileName = fileName;
        let counter = nextConsecutivo;
        while (
          allAudienciaImages.some(
            (existingFile) => existingFile.split("?")[0].split("/").pop() === finalFileName
          )
        ) {
          counter++;
          finalFileName = `${audienciaId}_${etapaNombre}_${campoNombre}_${counter}.${extension}`;
          console.log(`Archivo ${fileName} ya existe, probando con:`, finalFileName);
        }
        console.log("Nombre final del archivo:", finalFileName);
        // Subir imagen
        await BucketApi.uploadAudienciaImage(
          processedFile,
          finalFileName,
          procesoIdentificador,
          audienciaId
        );
        // Obtener URL pública con cache-buster
        const urlResponse = await BucketApi.getAudienciaFileUrlFresh(
          finalFileName,
          procesoIdentificador,
          audienciaId
        );
        const imageUrl = urlResponse.data.file_url;
        // Insertar en Quill
        if (quillRef?.current) {
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection();
          const index = range ? range.index : 0;
          quill.insertEmbed(index, "image", imageUrl);
          quill.setSelection(index + 1);
        }
        // Guardar info en estado de sesión
        const imageInfo = {
          fileName: finalFileName,
          originalName: file.name,
          url: imageUrl,
          campo: campoNombre,
          uploadedAt: new Date().toISOString(),
        };
        setUploadedImages((prev) => [...prev, imageInfo]);
        console.log("Imagen subida exitosamente:", imageInfo);
        // Callback opcional
        if (onImageUploaded) {
          try {
            onImageUploaded(imageInfo);
          } catch (cbErr) {
            console.warn("Error en onImageUploaded callback:", cbErr);
          }
        }
        return true;
      } catch (error) {
        console.error("Error subiendo imagen:", error);
        setImageError("Error al subir la imagen. Por favor intenta nuevamente.");
        return false;
      } finally {
        setUploadingImage(false);
      }
    },
    [procesoIdentificador, audienciaId, etapaNombre, uploadedImages]
  );

  const processImagesForPDF = useCallback(
    async (htmlContent) => {
      if (!htmlContent) return htmlContent;
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      const images = doc.querySelectorAll("img");
      const baseFolder = `procesos/${procesoIdentificador}/audiencias/${audienciaId}/imagenes/`;
      for (let img of images) {
        try {
          const src = img.getAttribute("src");
          if (src && src.includes(baseFolder)) {
            const response = await fetch(src);
            const blob = await response.blob();
            const base64 = await BucketApi.convertToBase64(blob);
            img.setAttribute("src", base64);
            const fileName = src.split("/").pop().split("?")[0];
            img.setAttribute("data-filename", fileName);
          }
        } catch (error) {
          console.error("Error procesando imagen para PDF:", error);
        }
      }
      return doc.body.innerHTML;
    },
    [procesoIdentificador, audienciaId]
  );

  const extractImageUrls = useCallback(
    (htmlContent) => {
      if (!htmlContent) return [];
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      const images = doc.querySelectorAll("img");
      // Ser más específico con el patrón de carpeta
      const baseFolder = `procesos/${procesoIdentificador}/audiencias/${audienciaId}/imagenes/`;
      return Array.from(images)
        .map((img) => img.getAttribute("src"))
        .filter((src) => {
          if (!src) return false;

          // Verificar que contiene la carpeta base de esta audiencia específica
          const containsBaseFolder = src.includes(baseFolder);
          console.log("Verificando imagen:", src, "contiene base folder:", containsBaseFolder);

          return containsBaseFolder;
        });
    },
    [procesoIdentificador, audienciaId]
  );

  const cleanOrphanImages = useCallback(
    async (contenidosCampos, etapa = etapaNombre) => {
      try {
        console.log("Iniciando limpieza de imágenes huérfanas...");
        console.log("Contenidos de campos para etapa:", etapa, contenidosCampos);
        // Extraer todas las URLs de imágenes del contenido SOLO de esta etapa
        const allContent = Object.values(contenidosCampos).join(" ");
        const currentImageUrls = extractImageUrls(allContent);
        console.log("URLs de imágenes encontradas en el contenido:", currentImageUrls);
        // Extraer solo los nombres de archivo que pertenecen a la etapa actual
        const etapaPrefix = `${audienciaId}_${etapa}_`;
        const currentFileNames = currentImageUrls
          .map((url) => {
            const fileName = url.split("/").pop().split("?")[0];
            console.log("Procesando URL:", url, "-> fileName:", fileName);

            // CAMBIO CRÍTICO: Solo incluir archivos que pertenecen a esta etapa
            if (fileName.startsWith(etapaPrefix)) {
              console.log("Archivo válido para etapa", etapa, ":", fileName);
              return fileName;
            } else {
              console.log("Archivo NO pertenece a etapa", etapa, ", ignorando:", fileName);
              return null;
            }
          })
          .filter(Boolean); // Eliminar nulls

        console.log("Nombres de archivos de la etapa actual a conservar:", currentFileNames);

        // OBTENER TODAS las imágenes de TODAS las etapas de la audiencia
        let allAudienciaImages = [];
        try {
          const allImagesResponse = await BucketApi.getAllAudienciaImages(
            procesoIdentificador,
            audienciaId
          );

          if (allImagesResponse?.data) {
            const allImagesData = allImagesResponse.data;
            if (Array.isArray(allImagesData)) {
              allAudienciaImages = allImagesData;
            } else if (allImagesData?.files && Array.isArray(allImagesData.files)) {
              allAudienciaImages = allImagesData.files;
            } else if (allImagesData?.objects && Array.isArray(allImagesData.objects)) {
              allAudienciaImages = allImagesData.objects;
            }

            // Normalizar nombres de archivo
            allAudienciaImages = allAudienciaImages
              .map((item) => {
                if (typeof item === "string") return item.split("/").pop().split("?")[0];
                if (item?.name) return item.name.split("/").pop().split("?")[0];
                if (item?.key) return item.key.split("/").pop().split("?")[0];
                return null;
              })
              .filter(Boolean);
          }
        } catch (error) {
          console.warn("No se pudo obtener la lista completa de imágenes de la audiencia:", error);
        }

        console.log("Todas las imágenes de la audiencia:", allAudienciaImages);

        // Separar imágenes por etapas de manera más robusta
        const imagenesPorEtapa = {
          etapaActual: [],
          otrasEtapas: [],
        };

        allAudienciaImages.forEach((fileName) => {
          if (!fileName) return;

          // Verificar si pertenece a la etapa actual
          if (fileName.startsWith(etapaPrefix)) {
            imagenesPorEtapa.etapaActual.push(fileName);
          } else if (fileName.includes(`${audienciaId}_`)) {
            // Es de otra etapa de la misma audiencia
            imagenesPorEtapa.otrasEtapas.push(fileName);
          }
          // Si no contiene audienciaId, no es de esta audiencia (ignorar)
        });

        console.log("Imágenes por etapa:", imagenesPorEtapa);

        // Determinar qué imágenes de la etapa actual son huérfanas
        const orphanImagesEtapaActual = imagenesPorEtapa.etapaActual.filter(
          (fileName) => !currentFileNames.includes(fileName)
        );

        console.log("Imágenes huérfanas de la etapa actual a eliminar:", orphanImagesEtapaActual);

        // CONSTRUIR LISTA FINAL: conservar TODAS las imágenes de otras etapas + imágenes válidas de etapa actual
        const imagenesToKeep = [
          ...imagenesPorEtapa.otrasEtapas, // TODAS las imágenes de otras etapas (CONSERVAR SIEMPRE)
          ...currentFileNames, // Solo las imágenes válidas de la etapa actual
        ];

        console.log("Lista final de imágenes a conservar:");
        console.log("- De otras etapas:", imagenesPorEtapa.otrasEtapas.length);
        console.log("- De etapa actual válidas:", currentFileNames.length);
        console.log("- Total a conservar:", imagenesToKeep.length);
        console.log("- Archivos a conservar:", imagenesToKeep);

        // VALIDACIÓN ADICIONAL: Asegurar que no estamos eliminando archivos de otras etapas
        const toDelete = allAudienciaImages.filter((img) => !imagenesToKeep.includes(img));
        const deletingFromOtherStages = toDelete.filter(
          (img) => img.includes(`${audienciaId}_`) && !img.startsWith(etapaPrefix)
        );

        if (deletingFromOtherStages.length > 0) {
          console.error(
            "ALERTA: Se intentó eliminar imágenes de otras etapas:",
            deletingFromOtherStages
          );
          console.error("CANCELANDO LIMPIEZA por seguridad");
          return {
            success: false,
            error:
              "Se detectó intento de eliminar imágenes de otras etapas. Operación cancelada por seguridad.",
            etapa_procesada: etapa,
          };
        }

        // Limpiar solo las imágenes huérfanas de la etapa actual
        const result = await BucketApi.cleanOrphanAudienciaImages(
          procesoIdentificador,
          audienciaId,
          imagenesToKeep
        );

        console.log("Resultado de limpieza:", result.data);

        return {
          success: true,
          etapa_procesada: etapa,
          imagenes_conservadas_etapa_actual: currentFileNames.length,
          imagenes_conservadas_otras_etapas: imagenesPorEtapa.otrasEtapas.length,
          imagenes_eliminadas_etapa_actual: orphanImagesEtapaActual.length,
          total_conservadas: imagenesToKeep.length,
          ...result.data,
        };
      } catch (error) {
        console.error("Error limpiando imágenes huérfanas:", error);
        return {
          success: false,
          error: error.message,
        };
      }
    },
    [procesoIdentificador, audienciaId, etapaNombre, extractImageUrls]
  );

  const getImagesList = useCallback(async () => {
    try {
      return await BucketApi.getAudienciaImagesList(procesoIdentificador, audienciaId, etapaNombre);
    } catch (error) {
      console.error("Error obteniendo lista de imágenes:", error);
      return { success: false, error: error.message, images: [] };
    }
  }, [procesoIdentificador, audienciaId, etapaNombre]);

  const deleteImage = useCallback(
    async (fileName) => {
      try {
        await BucketApi.deleteAudienciaImage(fileName, procesoIdentificador, audienciaId);
        setUploadedImages((prev) => prev.filter((img) => img.fileName !== fileName));
        return { success: true };
      } catch (error) {
        console.error("Error eliminando imagen:", error);
        return { success: false, error: error.message };
      }
    },
    [procesoIdentificador, audienciaId, etapaNombre]
  );

  const validateContent = useCallback(
    (htmlContent) => {
      const validation = { isValid: true, warnings: [], errors: [] };
      if (!htmlContent) {
        validation.isValid = false;
        validation.errors.push("El campo no puede estar vacío");
        return validation;
      }
      const imageUrls = extractImageUrls(htmlContent);
      if (imageUrls.length > 10) {
        validation.warnings.push(
          "El campo tiene muchas imágenes, esto puede afectar el rendimiento del PDF"
        );
      }
      const invalidImages = imageUrls.filter(
        (url) => !url.startsWith("http") && !url.startsWith("data:")
      );
      if (invalidImages.length > 0) {
        validation.warnings.push("Algunas imágenes pueden tener URLs inválidas");
      }
      return validation;
    },
    [extractImageUrls]
  );

  const getQuillModules = useCallback(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
      clipboard: { matchVisual: false },
    }),
    []
  );

  const getQuillFormats = useCallback(
    () => ["header", "bold", "italic", "underline", "list", "bullet", "indent", "link", "image"],
    []
  );

  const resetImageCounters = useCallback(() => {
    imageCountersRef.current = {};
  }, []);

  return {
    uploadingImage,
    imageError,
    uploadedImages,
    setupImageHandler,
    handleImageUpload,
    processImagesForPDF,
    extractImageUrls,
    cleanOrphanImages,
    getImagesList,
    deleteImage,
    validateContent,
    resetImageCounters,
    getQuillModules,
    getQuillFormats,
    clearError: () => setImageError(null),
    clearUploadedImages: () => setUploadedImages([]),
  };
};

export default useAudienciaEditorImages;
